import { getApi, postApi, putApi, deleteApi } from 'utils/Api';
import {
  GetTriggersResponse,
  GetActionsResponse,
  GetWorkflowsResponse,
  GetWorkflowResponse,
  CreateWorkflowRequest,
  CreateWorkflowResponse,
  UpdateWorkflowRequest,
  UpdateWorkflowResponse,
  DeleteWorkflowResponse,
  GetWorkflowsParams,
  GetStatisticsResponse,
  GetExecutionsParams,
  GetExecutionsResponse,
} from './types/aiWorkflowTypes';

// Query keys for react-query
export const aiWorkflowQueryKeys = {
  triggers: 'ai-workflows-triggers',
  actions: 'ai-workflows-actions',
  workflows: 'ai-workflows-list',
  workflow: 'ai-workflow-detail',
  statistics: 'ai-workflows-statistics',
  executions: 'ai-workflows-executions',
  workflowExecutions: 'ai-workflow-executions',
};

const aiWorkflowApis = {
  // Fetch available triggers
  getTriggers: () => {
    return getApi<GetTriggersResponse>({
      url: '/ai-workflows/triggers',
      responseSchema: {},
    });
  },

  // Fetch available actions
  getActions: () => {
    return getApi<GetActionsResponse>({
      url: '/ai-workflows/actions',
      responseSchema: {},
    });
  },

  // Fetch all workflows for the current user
  getWorkflows: (params?: GetWorkflowsParams) => {
    const searchParams = new URLSearchParams();
    if (params?.trigger) {
      searchParams.append('trigger', params.trigger);
    }
    if (params?.is_active !== undefined) {
      searchParams.append('is_active', String(params.is_active));
    }
    
    const queryString = searchParams.toString();
    const url = `/ai-workflows${queryString ? `?${queryString}` : ''}`;
    
    return getApi<GetWorkflowsResponse>({
      url,
      responseSchema: {},
    });
  },

  // Fetch a single workflow by ID
  getWorkflowById: (workflowId: number) => {
    return getApi<GetWorkflowResponse>({
      url: `/ai-workflows/${workflowId}`,
      responseSchema: {},
    });
  },

  // Create a new workflow
  createWorkflow: (requestData: CreateWorkflowRequest) => {
    return postApi<CreateWorkflowResponse, CreateWorkflowRequest>({
      url: '/ai-workflows',
      requestData,
      responseSchema: {},
    });
  },

  // Update an existing workflow
  updateWorkflow: ({
    workflowId,
    requestData,
  }: {
    workflowId: number;
    requestData: UpdateWorkflowRequest;
  }) => {
    return putApi<UpdateWorkflowResponse, UpdateWorkflowRequest>({
      url: `/ai-workflows/${workflowId}`,
      requestData,
      responseSchema: {},
    });
  },

  // Delete a workflow
  deleteWorkflow: (workflowId: number) => {
    return deleteApi<DeleteWorkflowResponse, never>({
      url: `/ai-workflows/${workflowId}`,
      responseSchema: {},
    });
  },

  // Get workflow statistics
  getStatistics: (days?: number) => {
    const url = days ? `/ai-workflows/statistics?days=${days}` : '/ai-workflows/statistics';
    return getApi<GetStatisticsResponse>({
      url,
      responseSchema: {},
    });
  },

  // Get all executions (activity log)
  getExecutions: (params?: GetExecutionsParams) => {
    const searchParams = new URLSearchParams();
    if (params?.workflow_id) searchParams.append('workflow_id', String(params.workflow_id));
    if (params?.status) searchParams.append('status', params.status);
    if (params?.trigger_entity_type) searchParams.append('trigger_entity_type', params.trigger_entity_type);
    if (params?.days) searchParams.append('days', String(params.days));
    if (params?.per_page) searchParams.append('per_page', String(params.per_page));
    
    const queryString = searchParams.toString();
    const url = `/ai-workflows/executions${queryString ? `?${queryString}` : ''}`;
    
    return getApi<GetExecutionsResponse>({
      url,
      responseSchema: {},
    });
  },

  // Get execution history for a specific workflow
  getWorkflowExecutions: ({
    workflowId,
    params,
  }: {
    workflowId: number;
    params?: Omit<GetExecutionsParams, 'workflow_id'>;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.days) searchParams.append('days', String(params.days));
    if (params?.per_page) searchParams.append('per_page', String(params.per_page));
    
    const queryString = searchParams.toString();
    const url = `/ai-workflows/${workflowId}/executions${queryString ? `?${queryString}` : ''}`;
    
    return getApi<GetExecutionsResponse>({
      url,
      responseSchema: {},
    });
  },
};

export { aiWorkflowApis };

